import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { X, Save } from 'lucide-react';
import Select from 'react-select';
import FroalaEditorComponent from 'react-froala-wysiwyg';
import { createFroalaConfig } from '../../../config/froalaConfig';
import { customSelectStylesLarge } from '../../../config/selectStyles';
import { NOTE_TEMPLATE_CATEGORIES } from './noteTemplatesConstants';

const NoteTemplateCreateModal = ({ show, onClose, onSuccess }) => {
    const [title, setTitle] = useState('');
    const [category, setCategory] = useState(NOTE_TEMPLATE_CATEGORIES[4]); // Default: "Notizen"
    const [template, setTemplate] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    // Froala config
    // Bild-Upload nur fuer Fussbereich-Vorlagen (Angebot/Auftrag/Rechnung),
    // da nur dort der PDF-Resolver laeuft. Andere Kategorien wuerden Bilder
    // erzeugen, die im PDF nicht (korrekt) erscheinen.
    const enableImage = [0, 1, 2].includes(category?.value);
    const froalaConfig = createFroalaConfig({
        height: 200,
        placeholderText: 'Notizvorlage hier eingeben...',
        enableImage
    });

    // Validate form
    const validate = () => {
        const newErrors = {};
        if (!title.trim()) {
            newErrors.title = 'Titel ist erforderlich';
        }
        if (!template.trim()) {
            newErrors.template = 'Notizvorlage ist erforderlich';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle submit
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) return;

        setIsSubmitting(true);

        try {
            const formData = new FormData();
            formData.append('title', title);
            formData.append('category', category.value);
            formData.append('template', template);

            const response = await fetch('/index.php/notestemplates/insert', {
                method: 'POST',
                body: formData
            });

            const result = await response.json();

            if (result.success) {
                onSuccess();
            } else {
                setErrors({ submit: result.message || 'Fehler beim Erstellen' });
            }
        } catch (error) {
            console.error('Error creating template:', error);
            setErrors({ submit: 'Fehler beim Erstellen der Notizvorlage' });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Modal styles
    const modalHeaderStyle = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1.25rem 1.5rem',
        borderBottom: '1px solid var(--border-light, #e2e8f0)',
        background: 'var(--bg-secondary, #f5f7f9)'
    };

    const modalTitleStyle = {
        fontSize: '1.125rem',
        fontWeight: '600',
        color: 'var(--text-primary, #2c3e50)',
        margin: 0
    };

    const closeButtonStyle = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '32px',
        height: '32px',
        border: 'none',
        borderRadius: '8px',
        background: 'transparent',
        color: 'var(--text-secondary, #64748b)',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
    };

    const modalBodyStyle = {
        padding: '1.5rem'
    };

    const formGroupStyle = {
        marginBottom: '1.25rem'
    };

    const labelStyle = {
        display: 'block',
        marginBottom: '0.5rem',
        fontSize: '0.875rem',
        fontWeight: '500',
        color: 'var(--text-primary, #2c3e50)'
    };

    const inputStyle = {
        width: '100%',
        padding: '0.625rem 1rem',
        border: '1px solid var(--border-medium, #cbd5e1)',
        borderRadius: '8px',
        fontSize: '0.875rem',
        color: 'var(--text-primary, #2c3e50)',
        background: 'var(--bg-card, #ffffff)',
        transition: 'all 0.2s ease'
    };

    const errorStyle = {
        color: '#ef4444',
        fontSize: '0.75rem',
        marginTop: '0.25rem'
    };

    const modalFooterStyle = {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '0.75rem',
        padding: '1.25rem 1.5rem',
        borderTop: '1px solid var(--border-light, #e2e8f0)',
        background: 'var(--bg-secondary, #f5f7f9)'
    };

    const cancelButtonStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.625rem 1.25rem',
        border: '1px solid var(--border-medium, #cbd5e1)',
        borderRadius: '8px',
        background: 'var(--bg-card, #ffffff)',
        color: 'var(--text-primary, #2c3e50)',
        fontSize: '0.875rem',
        fontWeight: '500',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
    };

    const submitButtonStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.625rem 1.25rem',
        border: 'none',
        borderRadius: '8px',
        background: 'var(--accent-slate, #5b7c8d)',
        color: 'white',
        fontSize: '0.875rem',
        fontWeight: '500',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
    };

    return (
        <Modal
            show={show}
            onHide={onClose}
            size="lg"
            centered
            backdrop="static"
        >
            <form onSubmit={handleSubmit}>
                {/* Header */}
                <div style={modalHeaderStyle}>
                    <h5 style={modalTitleStyle}>Notizvorlage erstellen</h5>
                    <button
                        type="button"
                        style={closeButtonStyle}
                        onClick={onClose}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-hover, #f1f5f9)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div style={modalBodyStyle}>
                    {/* Title */}
                    <div style={formGroupStyle}>
                        <label style={labelStyle}>
                            Titel <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        <input
                            type="text"
                            style={{
                                ...inputStyle,
                                borderColor: errors.title ? '#ef4444' : 'var(--border-medium, #cbd5e1)'
                            }}
                            value={title}
                            onChange={(e) => {
                                setTitle(e.target.value);
                                if (errors.title) setErrors({ ...errors, title: null });
                            }}
                            placeholder="Titel der Notizvorlage"
                        />
                        {errors.title && <div style={errorStyle}>{errors.title}</div>}
                    </div>

                    {/* Category */}
                    <div style={formGroupStyle}>
                        <label style={labelStyle}>
                            Kategorie <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        <Select
                            options={NOTE_TEMPLATE_CATEGORIES}
                            value={category}
                            onChange={setCategory}
                            styles={customSelectStylesLarge}
                            placeholder="Kategorie auswählen..."
                            isSearchable={false}
                        />
                    </div>

                    {/* Template */}
                    <div style={formGroupStyle}>
                        <label style={labelStyle}>
                            Notizvorlage <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        <FroalaEditorComponent
                            key={enableImage ? 'froala-img' : 'froala-noimg'}
                            tag='textarea'
                            config={froalaConfig}
                            model={template}
                            onModelChange={setTemplate}
                        />
                        {errors.template && <div style={errorStyle}>{errors.template}</div>}
                    </div>

                    {/* General Error */}
                    {errors.submit && (
                        <div style={{ ...errorStyle, padding: '0.75rem', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '8px' }}>
                            {errors.submit}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={modalFooterStyle}>
                    <button
                        type="button"
                        style={cancelButtonStyle}
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        Abbrechen
                    </button>
                    <button
                        type="submit"
                        style={{
                            ...submitButtonStyle,
                            opacity: isSubmitting ? 0.7 : 1,
                            cursor: isSubmitting ? 'not-allowed' : 'pointer'
                        }}
                        disabled={isSubmitting}
                    >
                        <Save size={18} />
                        {isSubmitting ? 'Speichert...' : 'Erstellen'}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default NoteTemplateCreateModal;
